import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useAuth } from "./AuthContext";
import { ReimbursementAccessRecord, reimbursementAccessService } from "../services/reimbursementAccessService";

interface ReimbursementAccessState {
  loading: boolean;
  error: string | null;
  access: ReimbursementAccessRecord | null;
  canRead: boolean;
  canWrite: boolean;
  canInitiate: boolean;
  canReview: boolean;
  canReview2: boolean;
  canRecommend: boolean;
  canSanction: boolean;
  canApproveAsRM: boolean;
}

const INITIAL_STATE: ReimbursementAccessState = {
  loading: true,
  error: null,
  access: null,
  canRead: false,
  canWrite: false,
  canInitiate: false,
  canReview: false,
  canReview2: false,
  canRecommend: false,
  canSanction: false,
  canApproveAsRM: false,
};

const ReimbursementAccessContext = createContext<ReimbursementAccessState>(INITIAL_STATE);

export const useReimbursementAccess = () => useContext(ReimbursementAccessContext);

export const ReimbursementAccessProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const [state, setState] = useState<ReimbursementAccessState>(INITIAL_STATE);

  useEffect(() => {
    let active = true;

    const loadAccess = async () => {
      if (!user?.id) {
        setState({ ...INITIAL_STATE, loading: false });
        return;
      }

      setState((prev) => ({ ...prev, loading: true, error: null }));
      try {
        const access = await reimbursementAccessService.getUserAccess();
        if (!active) return;

        const canRead = Boolean(access?.read_access || access?.write_access || access?.initiator || access?.is_reporting_manager || user.isadmin);
        const canWrite = Boolean(access?.write_access || user.isadmin);
        const canInitiate = Boolean(access?.initiator || user.isadmin);
        const canReview = Boolean(access?.reviewer || user.isadmin);
        const canReview2 = Boolean(access?.reviewer_2 || user.isadmin);
        const canRecommend = Boolean(access?.recommender || user.isadmin);
        const canSanction = Boolean(access?.sanction_authority_1 || user.isadmin);
        const canApproveAsRM = Boolean(access?.is_reporting_manager || user.isadmin);

        setState({
          loading: false,
          error: null,
          access,
          canRead,
          canWrite,
          canInitiate,
          canReview,
          canReview2,
          canRecommend,
          canSanction,
          canApproveAsRM,
        });
      } catch (error: any) {
        if (!active) return;
        setState({
          loading: false,
          error: error?.message || "Failed to load reimbursement access",
          access: null,
          canRead: Boolean(user?.isadmin),
          canWrite: Boolean(user?.isadmin),
          canInitiate: Boolean(user?.isadmin),
          canReview: Boolean(user?.isadmin),
          canReview2: Boolean(user?.isadmin),
          canRecommend: Boolean(user?.isadmin),
          canSanction: Boolean(user?.isadmin),
          canApproveAsRM: Boolean(user?.isadmin),
        });
      }
    };

    loadAccess();
    return () => {
      active = false;
    };
  }, [user?.id, user?.isadmin]);

  const value = useMemo(() => state, [state]);

  return <ReimbursementAccessContext.Provider value={value}>{children}</ReimbursementAccessContext.Provider>;
};
